import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Globe2, ExternalLink } from "lucide-react";
import ChartLegend, { useToggleableSeries } from "@/components/ChartLegend";
import ChartDateRange, { useChartDateRange } from "@/components/ChartDateRange";
import { formatBig } from "@/pages/Overview";

const COLORS = ["#002FA7", "#10B981", "#FFC107", "#E53935", "#0EA5E9", "#A855F7"];

// Metrics we will plot per property (taken from each AWStats upload's summary.totals).
const METRIC_KEYS = ["visits", "unique_visitors", "pages", "hits"];
const METRIC_LABELS = {
  visits: "Visits",
  unique_visitors: "Unique visitors",
  pages: "Pages",
  hits: "Hits",
};

// AWStats CSVs come in two flavours:
//  • The historical multi-month parser (`parse_awstats_history`) emits
//    snake_case keys: visits, unique_visitors, pages, hits, bandwidth_mb.
//  • A monthly daily-detail export uploaded via /uploads keeps the raw column
//    headers: "Number of visits", "Pages", "Hits", "Bandwidth",
//    "Unique visitors", etc.
// This helper reads from either shape into the canonical METRIC_KEYS so
// every property's chart populates regardless of which upload flow was used.
function readMetric(totals, canonicalKey) {
  if (!totals) return 0;
  // Fast path — already canonical.
  if (totals[canonicalKey] !== undefined) return Number(totals[canonicalKey] || 0);
  const aliases = {
    visits: ["number of visits", "visits", "sessions", "total visits"],
    unique_visitors: ["unique visitors", "unique_visitors", "unique visits", "unique users", "users"],
    pages: ["pages", "page views", "pageviews"],
    hits: ["hits", "total hits"],
  };
  const wanted = aliases[canonicalKey] || [canonicalKey];
  for (const [k, v] of Object.entries(totals)) {
    const norm = String(k).trim().toLowerCase();
    if (wanted.includes(norm)) return Number(v || 0);
  }
  return 0;
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function PerPropertyTrends({ clientId, masterRange }) {
  const [bus, setBus] = useState([]);
  const [seriesByBu, setSeriesByBu] = useState({}); // bu_id -> [{month, visits, ...}]
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // 1) load properties
        const { data: businessUnits } = await api.get(`/clients/${clientId}/business-units`);
        businessUnits.sort(
          (a, b) => (a.priority || 5) - (b.priority || 5) || a.name.localeCompare(b.name)
        );
        if (cancelled) return;
        setBus(businessUnits);

        // 2) one call grabs every awstats upload for the client; we group client-side
        const { data: ups } = await api.get(
          `/clients/${clientId}/uploads?platform=awstats`
        );
        if (cancelled) return;
        const byBu = {};
        for (const u of ups || []) {
          if (!u.business_unit_id) continue;
          const totals = u.summary?.totals || {};
          const row = { month: u.period_month };
          METRIC_KEYS.forEach((k) => { row[k] = readMetric(totals, k); });
          if (!byBu[u.business_unit_id]) byBu[u.business_unit_id] = [];
          byBu[u.business_unit_id].push(row);
        }
        // Dedupe per (bu, month): if a property has multiple AWStats uploads for
        // the same period (e.g. a bulk historical import + a fresh monthly upload),
        // take the MAX of each metric. Previously these stacked as duplicate
        // x-axis points, producing the dip-to-zero spikes seen in older builds.
        const dedupe = (rows) => {
          const merged = new Map();
          for (const r of rows) {
            const existing = merged.get(r.month);
            if (!existing) {
              merged.set(r.month, { ...r });
            } else {
              METRIC_KEYS.forEach((k) => {
                existing[k] = Math.max(existing[k] || 0, r[k] || 0);
              });
            }
          }
          return Array.from(merged.values());
        };
        for (const id of Object.keys(byBu)) {
          byBu[id] = dedupe(byBu[id]);
        }
        // sort each property's series by month ascending
        Object.values(byBu).forEach((arr) => arr.sort(
          (a, b) => a.month.localeCompare(b.month)
        ));
        setSeriesByBu(byBu);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  if (!loading && bus.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="per-property-trends">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Per-property</div>
          <h2 className="font-display font-bold text-xl tracking-tight">
            Historical traffic by property
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            {masterRange
              ? "Mini trend for every property using the imported AWStats history. Filtered by the master date range above."
              : "Mini trend for every property using the imported AWStats history. Each chart has its own date range + legend toggles."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {bus.map((bu) => (
          <PropertyTrendCard
            key={bu.business_unit_id}
            bu={bu}
            series={seriesByBu[bu.business_unit_id] || []}
            masterRange={masterRange}
          />
        ))}
      </div>
    </div>
  );
}

function PropertyTrendCard({ bu, series, masterRange }) {
  const imgSrc = bu.og_image_url ? `${BACKEND}${bu.og_image_url}` : null;
  const localRange = useChartDateRange("last12");
  const dateRange = masterRange || localRange;
  const legendToggle = useToggleableSeries();
  const visibleSeries = useMemo(() => dateRange.filter(series), [dateRange, series]);
  const actualRange = useMemo(() => {
    const sorted = series.map((r) => r.month).sort();
    return { startYM: sorted[0], endYM: sorted[sorted.length - 1] };
  }, [series]);

  const hasData = series.length > 0;

  // Which metrics actually have non-zero data anywhere in this property's
  // series? Hiding flat-zero lines is much better than rendering them as a
  // misleading "drop to zero" — e.g. AWStats daily-detail exports don't carry
  // the "Unique visitors" column, so plotting 0 misled the eye.
  const presentMetrics = useMemo(() => {
    return METRIC_KEYS.filter((k) => series.some((r) => Number(r[k] || 0) > 0));
  }, [series]);

  return (
    <Card
      className="rounded-sm border-zinc-200 bg-white"
      data-testid={`per-property-trend-${bu.business_unit_id}`}
    >
      <CardContent className="p-5">
        {/* Header row */}
        <div className="flex items-center gap-3 mb-3">
          <div className="relative h-10 w-10 rounded-sm overflow-hidden flex-shrink-0 bg-zinc-100 border border-zinc-200">
            {imgSrc ? (
              <img
                src={imgSrc}
                alt={bu.name}
                className="h-full w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="h-full w-full bg-zinc-900 text-white flex items-center justify-center">
                <Globe2 className="h-4 w-4" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-bold text-sm">{bu.name}</span>
              <span
                className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm bg-[#002FA7]/10 text-[#002FA7]"
                title="Property priority"
              >
                P{bu.priority ?? 5}
              </span>
            </div>
            <a
              href={bu.website_url}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-zinc-500 inline-flex items-center gap-1 hover:text-[#002FA7]"
            >
              {bu.website_url} <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          {hasData && !masterRange && (
            <ChartDateRange
              {...dateRange}
              actualRange={actualRange}
              testIdPrefix={`per-property-range-${bu.business_unit_id}`}
            />
          )}
        </div>

        {hasData ? (
          <>
            <ChartLegend
              keys={METRIC_KEYS}
              colors={COLORS}
              labels={METRIC_LABELS}
              hidden={legendToggle.hidden}
              onToggle={legendToggle.toggle}
              testIdPrefix={`per-property-legend-${bu.business_unit_id}`}
            />
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={visibleSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} tickFormatter={formatBig} />
                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7" }} />
                {METRIC_KEYS.map((k, idx) => (
                  <Line
                    key={k}
                    type="monotone"
                    dataKey={k}
                    stroke={COLORS[idx % COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    hide={legendToggle.isHidden(k)}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </>
        ) : (
          <div className="h-[200px] grid place-items-center text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-sm">
            No historical AWStats data uploaded for this property yet.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
